import React, { useState, useEffect } from 'react';
import { 
  Save, Bell, Lock, Globe, Palette, Clock, Building, ChevronRight,
  Plus, Trash2, Edit2, CheckCircle, XCircle, Loader2, MessageCircle,
  Facebook, Mail, ExternalLink, Copy, Eye, EyeOff, ThumbsUp, GitBranch, FileText, Users, Archive
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  getWhatsAppChannels, createWhatsAppChannel, updateWhatsAppChannel, deleteWhatsAppChannel, testWhatsAppChannel,
  getFacebookChannels, createFacebookChannel, updateFacebookChannel, deleteFacebookChannel, testFacebookChannel,
  getEmailChannels, createEmailChannel, updateEmailChannel, deleteEmailChannel, testEmailChannel,
  getSettings, updateGeneralSettings, updateNotificationSettings, updateSecuritySettings, updateBusinessHoursSettings,
  getSatisfactionSettings, updateSatisfactionSettings,
  getTwilioConfig, saveTwilioConfig, testTwilioConfig, getTwilioWebhookUrl,
  getRoutingRules, createRoutingRule, updateRoutingRule, deleteRoutingRule,
  getUsers, getTemplates, createTemplate, updateTemplate, deleteTemplate, updateUser,
  getArchivedConversations, getArchiveStats, restoreArchivedConversation,
  changePassword, toggleMFA, getMyProfile
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const settingsSections = [
  { id: 'general', label: 'General', icon: Building },
  { id: 'channels', label: 'Channels', icon: Globe },
  { id: 'routing', label: 'Routing', icon: GitBranch },
  { id: 'users', label: 'User Management', icon: Users },
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'archive', label: 'Archive', icon: Archive },
  { id: 'satisfaction', label: 'Customer Satisfaction', icon: ThumbsUp },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'business-hours', label: 'Business Hours', icon: Clock },
  { id: 'appearance', label: 'Appearance', icon: Palette }
];

const SettingsView = () => {
  const { isAdmin, user } = useAuth();
  const [activeSection, setActiveSection] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // MFA state
  const [userMfaEnabled, setUserMfaEnabled] = useState(false);
  const [togglingMfa, setTogglingMfa] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    general: { company_name: 'Post Aruba', support_email: 'support@postaruba.com', timezone: 'America/Aruba', language: 'en' },
    notifications: { email_notifications: true, push_notifications: true, sound_alerts: true },
    security: { two_factor_auth: false, session_timeout: 30 },
    business_hours: { start_time: '08:00', end_time: '18:00', work_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] }
  });

  // Channel states
  const [whatsappChannels, setWhatsappChannels] = useState([]);
  const [facebookChannels, setFacebookChannels] = useState([]);
  const [emailChannels, setEmailChannels] = useState([]);
  
  // Twilio config state
  const [twilioConfig, setTwilioConfig] = useState({ account_sid: '', auth_token: '', configured: false, is_connected: false });
  const [twilioEditing, setTwilioEditing] = useState(false);
  const [twilioSid, setTwilioSid] = useState('');
  const [twilioToken, setTwilioToken] = useState('');
  const [twilioTesting, setTwilioTesting] = useState(false);
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');

  // Routing state
  const [routingRules, setRoutingRules] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [routingDialog, setRoutingDialog] = useState({ open: false, editing: null });

  // Templates state
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState({ open: false, editing: null });
  
  // User WhatsApp Sender Management state
  const [userSenderDialog, setUserSenderDialog] = useState({ open: false, user: null });
  
  // Satisfaction Survey state
  const [satisfactionSettings, setSatisfactionSettings] = useState({
    enabled: false,
    auto_send_on_resolve: true,
    survey_message: "Thank you for contacting Post Aruba Support! We'd love to hear your feedback. How would you rate your experience? Please reply with a number from 1-5 (1=Poor, 5=Excellent)",
    questions: [],
    thank_you_message: "Thank you for your feedback! We appreciate your time."
  });
  
  // Archive state
  const [archiveStats, setArchiveStats] = useState(null);
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  
  // Dialog states
  const [channelDialog, setChannelDialog] = useState({ open: false, type: null, editing: null });
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsData, whatsapp, facebook, email, satisfactionData, twilioCfg, webhookData, rules, users, tpls] = await Promise.all([
        getSettings(),
        getWhatsAppChannels(),
        getFacebookChannels(),
        getEmailChannels(),
        getSatisfactionSettings(),
        getTwilioConfig().catch(() => ({ configured: false })),
        getTwilioWebhookUrl().catch(() => ({ webhook_url: '' })),
        getRoutingRules().catch(() => []),
        getUsers().catch(() => []),
        getTemplates().catch(() => [])
      ]);
      setSettings(settingsData);
      setWhatsappChannels(whatsapp);
      setFacebookChannels(facebook);
      setEmailChannels(email);
      setSatisfactionSettings(satisfactionData);
      setTwilioConfig(twilioCfg);
      setTwilioSid(twilioCfg.account_sid || '');
      setWebhookUrl(webhookData.webhook_url || '');
      setRoutingRules(rules);
      setAllUsers(users);
      setMessageTemplates(tpls);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load user MFA status on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profile = await getMyProfile();
        setUserMfaEnabled(profile.mfa_enabled || false);
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
    loadUserProfile();
  }, []);

  // Password change handler
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordForm.new.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(passwordForm.current, passwordForm.new);
      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // MFA toggle handler
  const handleToggleMfa = async (enable) => {
    setTogglingMfa(true);
    try {
      await toggleMFA(enable);
      setUserMfaEnabled(enable);
    } catch (error) {
      console.error('Failed to toggle MFA:', error);
    } finally {
      setTogglingMfa(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      switch (activeSection) {
        case 'general':
          await updateGeneralSettings(settings.general);
          break;
        case 'notifications':
          await updateNotificationSettings(settings.notifications);
          break;
        case 'security':
          await updateSecuritySettings(settings.security);
          break;
        case 'business-hours':
          await updateBusinessHoursSettings(settings.business_hours);
          break;
        case 'satisfaction':
          await updateSatisfactionSettings(satisfactionSettings);
          break;
      }
      alert('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleSecret = (key) => {
    setShowSecrets({ ...showSecrets, [key]: !showSecrets[key] });
  };

  // Channel management functions
  const handleSaveChannel = async (type, data) => {
    try {
      // For WhatsApp, map fields to backend shape
      const payload = type === 'whatsapp' 
        ? { name: data.name, phone_number: data.whatsapp_number || data.phone_number, is_default: data.is_default || false }
        : data;

      if (channelDialog.editing) {
        switch (type) {
          case 'whatsapp':
            await updateWhatsAppChannel(channelDialog.editing.id, payload);
            break;
          case 'facebook':
            await updateFacebookChannel(channelDialog.editing.id, payload);
            break;
          case 'email':
            await updateEmailChannel(channelDialog.editing.id, payload);
            break;
        }
      } else {
        switch (type) {
          case 'whatsapp':
            await createWhatsAppChannel(payload);
            break;
          case 'facebook':
            await createFacebookChannel(payload);
            break;
          case 'email':
            await createEmailChannel(payload);
            break;
        }
      }
      await loadData();
      setChannelDialog({ open: false, type: null, editing: null });
    } catch (error) {
      console.error('Failed to save channel:', error);
      alert('Failed to save channel');
    }
  };

  const handleDeleteChannel = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) return;
    try {
      switch (type) {
        case 'whatsapp':
          await deleteWhatsAppChannel(id);
          break;
        case 'facebook':
          await deleteFacebookChannel(id);
          break;
        case 'email':
          await deleteEmailChannel(id);
          break;
      }
      await loadData();
    } catch (error) {
      console.error('Failed to delete channel:', error);
    }
  };

  const handleTestChannel = async (type, id) => {
    try {
      let result;
      switch (type) {
        case 'whatsapp':
          result = await testWhatsAppChannel(id);
          break;
        case 'facebook':
          result = await testFacebookChannel(id);
          break;
        case 'email':
          result = await testEmailChannel(id);
          break;
      }
      alert(result.message);
      await loadData();
    } catch (error) {
      alert('Connection test failed');
    }
  };

  const handleSaveTwilioConfig = async () => {
    setTwilioSaving(true);
    try {
      await saveTwilioConfig({ account_sid: twilioSid, auth_token: twilioToken });
      setTwilioEditing(false);
      setTwilioToken('');
      await loadData();
      alert('Twilio credentials saved');
    } catch (error) {
      alert('Failed to save Twilio config');
    } finally {
      setTwilioSaving(false);
    }
  };

  const handleTestTwilio = async () => {
    setTwilioTesting(true);
    try {
      const result = await testTwilioConfig();
      alert(result.success ? result.message : `Test failed: ${result.message}`);
      if (result.success) await loadData();
    } catch (error) {
      alert('Connection test failed');
    } finally {
      setTwilioTesting(false);
    }
  };

  // Routing handlers
  const handleSaveRoutingRule = async (data) => {
    try {
      if (routingDialog.editing) {
        await updateRoutingRule(routingDialog.editing.id, data);
      } else {
        await createRoutingRule(data);
      }
      await loadData();
      setRoutingDialog({ open: false, editing: null });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save routing rule');
    }
  };

  const handleDeleteRoutingRule = async (id) => {
    if (!window.confirm('Delete this routing rule?')) return;
    try {
      await deleteRoutingRule(id);
      await loadData();
    } catch (error) {
      alert('Failed to delete routing rule');
    }
  };

  const getAgentName = (id) => {
    const u = allUsers.find(u => u.id === id);
    return u ? u.name : 'Unassigned';
  };

  const getSenderName = (senderId) => {
    const s = whatsappChannels.find(s => s.id === senderId);
    return s ? `${s.name} (${s.phone_number})` : senderId;
  };

  const renderRoutingSection = () => {
    const platformRules = routingRules.filter(r => r.rule_type === 'platform');
    const senderRules = routingRules.filter(r => r.rule_type === 'whatsapp_sender');
    const platformLabels = { whatsapp: 'WhatsApp', email: 'Email', facebook: 'Facebook' };
    const platformIcons = { whatsapp: MessageCircle, email: Mail, facebook: Facebook };

    return (
      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Conversation Routing</h3>
          <p className="text-sm text-gray-500 mb-6">Automatically assign incoming conversations to agents by platform or WhatsApp sender number. If the primary agent is offline, the secondary agent will receive the conversation.</p>
        </div>

        {/* Platform Routing */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-800">Platform Routing</h4>
            <Button size="sm" className="gap-2 bg-[#393d8a] hover:bg-[#393d8a]/90" onClick={() => setRoutingDialog({ open: true, editing: null, ruleType: 'platform' })} data-testid="add-platform-rule-btn">
              <Plus className="w-4 h-4" /> Add Rule
            </Button>
          </div>
          <div className="space-y-3">
            {platformRules.map(rule => {
              const PIcon = platformIcons[rule.platform] || Mail;
              return (
                <div key={rule.id} className="p-4 border border-gray-200 rounded-lg bg-white flex items-center justify-between" data-testid={`routing-rule-${rule.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <PIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{platformLabels[rule.platform] || rule.platform}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                        <span>Primary: <strong className="text-gray-700">{getAgentName(rule.primary_agent_id)}</strong></span>
                        {rule.secondary_agent_id && <span>Secondary: <strong className="text-gray-700">{getAgentName(rule.secondary_agent_id)}</strong></span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => setRoutingDialog({ open: true, editing: rule, ruleType: 'platform' })}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteRoutingRule(rule.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            {platformRules.length === 0 && (
              <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">No platform routing rules. Conversations will remain unassigned until manually assigned.</p>
            )}
          </div>
        </div>

        {/* WhatsApp Sender Routing */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-800">WhatsApp Sender Routing</h4>
            <Button size="sm" className="gap-2 bg-[#30a34b] hover:bg-[#30a34b]/90" onClick={() => setRoutingDialog({ open: true, editing: null, ruleType: 'whatsapp_sender' })} data-testid="add-sender-rule-btn" disabled={whatsappChannels.length === 0}>
              <Plus className="w-4 h-4" /> Add Rule
            </Button>
          </div>
          <div className="space-y-3">
            {senderRules.map(rule => (
              <div key={rule.id} className="p-4 border border-gray-200 rounded-lg bg-white flex items-center justify-between" data-testid={`routing-rule-${rule.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{getSenderName(rule.whatsapp_sender_id)}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                      <span>Primary: <strong className="text-gray-700">{getAgentName(rule.primary_agent_id)}</strong></span>
                      {rule.secondary_agent_id && <span>Secondary: <strong className="text-gray-700">{getAgentName(rule.secondary_agent_id)}</strong></span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setRoutingDialog({ open: true, editing: rule, ruleType: 'whatsapp_sender' })}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteRoutingRule(rule.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {senderRules.length === 0 && (
              <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">
                {whatsappChannels.length === 0 ? 'Add WhatsApp senders in Channels first.' : 'No sender-specific routing rules. Platform-level WhatsApp routing will be used.'}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Template handlers
  const handleSaveTemplate = async (data) => {
    try {
      if (templateDialog.editing) {
        await updateTemplate(templateDialog.editing.id, data);
      } else {
        await createTemplate(data);
      }
      await loadData();
      setTemplateDialog({ open: false, editing: null });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await deleteTemplate(id);
      await loadData();
    } catch (error) {
      alert('Failed to delete template');
    }
  };

  const renderTemplatesSection = () => {
    const categories = [...new Set(messageTemplates.map(t => t.category || 'general'))];
    const channelLabels = { all: 'All', whatsapp: 'WhatsApp', facebook: 'Facebook', email: 'Email' };

    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-gray-900">Message Templates</h3>
            <Button size="sm" className="gap-2 bg-[#393d8a] hover:bg-[#393d8a]/90" onClick={() => setTemplateDialog({ open: true, editing: null })} data-testid="add-template-btn">
              <Plus className="w-4 h-4" /> Add Template
            </Button>
          </div>
          <p className="text-sm text-gray-500 mb-6">Create reusable message templates that agents can insert into conversations with one click. Agents can edit the text before sending.</p>
        </div>

        {categories.length === 0 && (
          <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">No templates created yet. Add your first template to help agents reply faster.</p>
        )}

        {categories.map(cat => (
          <div key={cat}>
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{cat}</h4>
            <div className="space-y-2">
              {messageTemplates.filter(t => (t.category || 'general') === cat).map(t => (
                <div key={t.id} className="p-4 border border-gray-200 rounded-lg bg-white" data-testid={`template-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">{t.name}</p>
                        <div className="flex gap-1">
                          {(t.channels || ['all']).map(ch => (
                            <span key={ch} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{channelLabels[ch] || ch}</span>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">{t.content}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTemplateDialog({ open: true, editing: t })} data-testid={`edit-template-${t.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // User Management handlers
  const handleUpdateUserSenders = async (userId, allowedSenders) => {
    try {
      await updateUser(userId, { allowed_whatsapp_senders: allowedSenders });
      await loadData();
      setUserSenderDialog({ open: false, user: null });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user WhatsApp permissions');
    }
  };

  const renderUsersSection = () => {
    const agents = allUsers.filter(u => u.role === 'agent');
    
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">User Management</h3>
          <p className="text-sm text-gray-500 mb-6">Manage agent permissions for WhatsApp senders. Restricting agents to specific senders ensures they can only reply from designated numbers.</p>
        </div>

        {/* WhatsApp Sender Permissions */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-4">WhatsApp Sender Permissions</h4>
          <div className="space-y-3">
            {agents.length === 0 && (
              <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">No agents found. Create agent accounts to manage their permissions.</p>
            )}
            {agents.map(agent => {
              const agentSenders = agent.allowed_whatsapp_senders || [];
              const hasRestrictions = agentSenders.length > 0;
              
              return (
                <div key={agent.id} className="p-4 border border-gray-200 rounded-lg bg-white" data-testid={`user-sender-card-${agent.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center text-white font-medium",
                        agent.status === 'online' ? 'bg-green-500' : 
                        agent.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
                      )}>
                        {agent.avatar || agent.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{agent.name}</p>
                        <p className="text-sm text-gray-500">{agent.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {hasRestrictions ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-medium">
                              Restricted to {agentSenders.length} sender{agentSenders.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                            All senders allowed
                          </span>
                        )}
                        {hasRestrictions && (
                          <div className="flex flex-wrap gap-1 mt-1 max-w-[250px] justify-end">
                            {agentSenders.slice(0, 2).map(phone => {
                              const sender = whatsappChannels.find(s => s.phone_number === phone);
                              return (
                                <span key={phone} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded truncate max-w-[100px]">
                                  {sender?.name || phone}
                                </span>
                              );
                            })}
                            {agentSenders.length > 2 && (
                              <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                +{agentSenders.length - 2} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setUserSenderDialog({ open: true, user: agent })}
                        data-testid={`edit-user-senders-${agent.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {whatsappChannels.length === 0 && (
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Configure WhatsApp senders in the Channels section first to manage agent permissions.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Archive section helpers
  const loadArchiveData = async () => {
    setArchiveLoading(true);
    try {
      const [stats, convs] = await Promise.all([
        getArchiveStats(),
        getArchivedConversations(null, 20, 0)
      ]);
      setArchiveStats(stats);
      setArchivedConversations(convs.conversations || []);
    } catch (e) {
      console.error('Failed to load archive data:', e);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleRestoreConversation = async (convId) => {
    setRestoring(convId);
    try {
      await restoreArchivedConversation(convId);
      await loadArchiveData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to restore conversation');
    } finally {
      setRestoring(null);
    }
  };

  const renderArchiveSection = () => {
    // Load archive data on first render of this section
    if (!archiveStats && !archiveLoading) {
      loadArchiveData();
    }

    const channelIcons = {
      email: <Mail className="w-4 h-4" />,
      whatsapp: <MessageCircle className="w-4 h-4 text-green-600" />,
      facebook: <Facebook className="w-4 h-4 text-blue-600" />
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Conversation Archive</h3>
          <p className="text-sm text-gray-500 mb-6">
            Resolved conversations are automatically archived after 48 hours and permanently deleted after 30 days.
          </p>
        </div>

        {/* Archive Stats */}
        {archiveLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : archiveStats ? (
          <>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-gray-900">{archiveStats.total_archived}</p>
                <p className="text-sm text-gray-500">Total Archived</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-blue-600">{archiveStats.by_channel?.email || 0}</p>
                <p className="text-sm text-gray-500">Email</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-green-600">{archiveStats.by_channel?.whatsapp || 0}</p>
                <p className="text-sm text-gray-500">WhatsApp</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-orange-600">{archiveStats.expiring_in_7_days}</p>
                <p className="text-sm text-gray-500">Expiring Soon</p>
              </div>
            </div>

            {/* Archived Conversations List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-800">Recent Archives</h4>
                <Button variant="outline" size="sm" onClick={loadArchiveData}>
                  <Loader2 className={cn("w-4 h-4 mr-2", archiveLoading && "animate-spin")} />
                  Refresh
                </Button>
              </div>
              
              {archivedConversations.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Archive className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No archived conversations</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {archivedConversations.map(conv => (
                    <div key={conv.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border">
                          {channelIcons[conv.channel] || <Mail className="w-4 h-4" />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{conv.customer?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{conv.subject || conv.last_message?.slice(0, 50) || 'No subject'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">
                          Archived: {new Date(conv.archived_at).toLocaleDateString()}
                        </span>
                        {isAdmin && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRestoreConversation(conv.id)}
                            disabled={restoring === conv.id}
                          >
                            {restoring === conv.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Restore'
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Archived conversations are permanently deleted after 30 days. 
                Use the "Restore" button to move a conversation back to the active inbox if needed.
              </p>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const renderChannelsSection = () => (
    <div className="space-y-8">
      {/* Twilio Account Config */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">WhatsApp via Twilio</h3>
            <p className="text-sm text-gray-500">Account-level Twilio credentials</p>
          </div>
        </div>

        <div className="p-4 border border-gray-200 rounded-lg bg-white space-y-4" data-testid="twilio-config-section">
          {!twilioEditing && twilioConfig.configured ? (
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Account SID:</span>
                  <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">{twilioConfig.account_sid}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Auth Token:</span>
                  <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">{twilioConfig.auth_token_set ? '••••••••' : 'Not set'}</code>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {twilioConfig.is_connected ? (
                    <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="w-3 h-3" /> Connected</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-500"><XCircle className="w-3 h-3" /> Not tested</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleTestTwilio} disabled={twilioTesting} data-testid="twilio-test-btn">
                  {twilioTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { setTwilioEditing(true); setTwilioSid(twilioConfig.account_sid || ''); setTwilioToken(''); }}>
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Account SID</Label>
                <Input value={twilioSid} onChange={(e) => setTwilioSid(e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-sid-input" />
              </div>
              <div className="space-y-2">
                <Label>Auth Token</Label>
                <div className="relative">
                  <Input
                    type={showSecrets.twilioToken ? 'text' : 'password'}
                    value={twilioToken}
                    onChange={(e) => setTwilioToken(e.target.value)}
                    placeholder="Enter Auth Token"
                    className="pr-10"
                    data-testid="twilio-token-input"
                  />
                  <button type="button" onClick={() => toggleSecret('twilioToken')} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showSecrets.twilioToken ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                {twilioConfig.configured && (
                  <Button variant="outline" size="sm" onClick={() => setTwilioEditing(false)}>Cancel</Button>
                )}
                <Button size="sm" className="bg-[#30a34b] hover:bg-[#30a34b]/90" onClick={handleSaveTwilioConfig} disabled={twilioSaving || !twilioSid || !twilioToken} data-testid="twilio-save-btn">
                  {twilioSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Credentials
                </Button>
              </div>
            </div>
          )}

          {/* Webhook URL */}
          {webhookUrl && (
            <div className="p-3 bg-blue-50 rounded-lg" data-testid="twilio-webhook-info">
              <p className="text-sm text-blue-700 font-medium mb-1">Twilio Webhook URL</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white px-2 py-1 rounded border border-blue-200 flex-1 truncate">{webhookUrl}</code>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(webhookUrl); }}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-xs text-blue-600 mt-1">Set this URL in your Twilio WhatsApp Sandbox or Sender settings as the webhook for incoming messages (HTTP POST).</p>
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Sender Numbers */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">WhatsApp Senders</h3>
              <p className="text-sm text-gray-500">{whatsappChannels.length} sender(s) configured</p>
            </div>
          </div>
          <Button 
            size="sm" 
            className="gap-2 bg-[#30a34b] hover:bg-[#30a34b]/90"
            onClick={() => setChannelDialog({ open: true, type: 'whatsapp', editing: null })}
            data-testid="add-whatsapp-sender-btn"
          >
            <Plus className="w-4 h-4" />
            Add Sender
          </Button>
        </div>
        
        <div className="space-y-3">
          {whatsappChannels.map((channel) => (
            <ChannelCard 
              key={channel.id}
              channel={channel}
              type="whatsapp"
              icon={<MessageCircle className="w-4 h-4 text-green-600" />}
              onEdit={() => setChannelDialog({ open: true, type: 'whatsapp', editing: channel })}
              onDelete={() => handleDeleteChannel('whatsapp', channel.id)}
              onTest={() => handleTestChannel('whatsapp', channel.id)}
            />
          ))}
          {whatsappChannels.length === 0 && (
            <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">No WhatsApp senders configured. Add a sender number to start receiving messages.</p>
          )}
        </div>
      </div>

      {/* Facebook Messenger Pages */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Facebook className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Facebook Messenger via Twilio</h3>
              <p className="text-sm text-gray-500">{facebookChannels.length} page(s) configured</p>
            </div>
          </div>
          <Button 
            size="sm" 
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={() => setChannelDialog({ open: true, type: 'facebook', editing: null })}
            data-testid="add-facebook-page-btn"
          >
            <Plus className="w-4 h-4" />
            Add Page
          </Button>
        </div>
        
        <div className="space-y-3">
          {facebookChannels.map((channel) => (
            <ChannelCard 
              key={channel.id}
              channel={channel}
              type="facebook"
              icon={<Facebook className="w-4 h-4 text-blue-600" />}
              onEdit={() => setChannelDialog({ open: true, type: 'facebook', editing: channel })}
              onDelete={() => handleDeleteChannel('facebook', channel.id)}
              onTest={() => handleTestChannel('facebook', channel.id)}
            />
          ))}
          {facebookChannels.length === 0 && (
            <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">No Facebook pages configured. Connect a Facebook Page via Twilio to start receiving Messenger messages.</p>
          )}
        </div>
      </div>

      {/* Email Channels */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Email</h3>
              <p className="text-sm text-gray-500">{emailChannels.length} channel(s) configured</p>
            </div>
          </div>
          <Button 
            size="sm" 
            className="gap-2 bg-[#eeb224] hover:bg-[#eeb224]/90"
            onClick={() => setChannelDialog({ open: true, type: 'email', editing: null })}
          >
            <Plus className="w-4 h-4" />
            Add Email
          </Button>
        </div>
        
        <div className="space-y-3">
          {emailChannels.map((channel) => (
            <ChannelCard 
              key={channel.id}
              channel={channel}
              type="email"
              icon={<Mail className="w-4 h-4 text-orange-600" />}
              onEdit={() => setChannelDialog({ open: true, type: 'email', editing: channel })}
              onDelete={() => handleDeleteChannel('email', channel.id)}
              onTest={() => handleTestChannel('email', channel.id)}
            />
          ))}
          {emailChannels.length === 0 && (
            <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">No Email channels configured</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={settings.general?.company_name || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    general: { ...settings.general, company_name: e.target.value }
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Support Email</Label>
                <Input
                  type="email"
                  value={settings.general?.support_email || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    general: { ...settings.general, support_email: e.target.value }
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select
                  value={settings.general?.timezone || 'America/Aruba'}
                  onValueChange={(value) => setSettings({
                    ...settings,
                    general: { ...settings.general, timezone: value }
                  })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Aruba">America/Aruba (AST)</SelectItem>
                    <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                    <SelectItem value="Europe/Amsterdam">Europe/Amsterdam (CET)</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Default Language</Label>
                <Select
                  value={settings.general?.language || 'en'}
                  onValueChange={(value) => setSettings({
                    ...settings,
                    general: { ...settings.general, language: value }
                  })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="nl">Dutch</SelectItem>
                    <SelectItem value="pa">Papiamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 'channels':
        return renderChannelsSection();

      case 'routing':
        return renderRoutingSection();

      case 'users':
        return renderUsersSection();

      case 'archive':
        return renderArchiveSection();

      case 'templates':
        return renderTemplatesSection();

      case 'satisfaction':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Satisfaction Survey</h3>
              <p className="text-sm text-gray-500">Configure automated satisfaction surveys sent after resolving conversations</p>
            </div>
            
            {/* Enable Survey */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Enable Satisfaction Surveys</p>
                <p className="text-sm text-gray-500">Collect feedback from customers after interactions</p>
              </div>
              <Switch
                checked={satisfactionSettings.enabled}
                onCheckedChange={(checked) => setSatisfactionSettings({
                  ...satisfactionSettings,
                  enabled: checked
                })}
              />
            </div>

            {satisfactionSettings.enabled && (
              <>
                {/* Auto-send on Resolve */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Auto-send on Resolve</p>
                    <p className="text-sm text-gray-500">Automatically send survey when a conversation is marked as resolved</p>
                  </div>
                  <Switch
                    checked={satisfactionSettings.auto_send_on_resolve}
                    onCheckedChange={(checked) => setSatisfactionSettings({
                      ...satisfactionSettings,
                      auto_send_on_resolve: checked
                    })}
                  />
                </div>

                {/* Survey Message */}
                <div className="space-y-2">
                  <Label>Survey Message</Label>
                  <Textarea
                    value={satisfactionSettings.survey_message}
                    onChange={(e) => setSatisfactionSettings({
                      ...satisfactionSettings,
                      survey_message: e.target.value
                    })}
                    placeholder="Enter the message to send to customers..."
                    rows={4}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500">This message will be sent to customers when asking for feedback</p>
                </div>

                {/* Custom Questions */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Custom Questions (Optional)</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newQuestion = {
                          id: `q_${Date.now()}`,
                          question: '',
                          type: 'rating',
                          required: true
                        };
                        setSatisfactionSettings({
                          ...satisfactionSettings,
                          questions: [...(satisfactionSettings.questions || []), newQuestion]
                        });
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Question
                    </Button>
                  </div>
                  
                  {(satisfactionSettings.questions || []).map((q, index) => (
                    <div key={q.id} className="p-4 border border-gray-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">Question {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => {
                            setSatisfactionSettings({
                              ...satisfactionSettings,
                              questions: satisfactionSettings.questions.filter((_, i) => i !== index)
                            });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        value={q.question}
                        onChange={(e) => {
                          const updated = [...satisfactionSettings.questions];
                          updated[index] = { ...updated[index], question: e.target.value };
                          setSatisfactionSettings({ ...satisfactionSettings, questions: updated });
                        }}
                        placeholder="Enter your question..."
                      />
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Label className="text-xs">Response Type</Label>
                          <Select
                            value={q.type}
                            onValueChange={(value) => {
                              const updated = [...satisfactionSettings.questions];
                              updated[index] = { ...updated[index], type: value };
                              setSatisfactionSettings({ ...satisfactionSettings, questions: updated });
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="rating">Rating (1-5)</SelectItem>
                              <SelectItem value="text">Text Response</SelectItem>
                              <SelectItem value="yes_no">Yes/No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                          <Switch
                            checked={q.required}
                            onCheckedChange={(checked) => {
                              const updated = [...satisfactionSettings.questions];
                              updated[index] = { ...updated[index], required: checked };
                              setSatisfactionSettings({ ...satisfactionSettings, questions: updated });
                            }}
                          />
                          <Label className="text-xs">Required</Label>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!satisfactionSettings.questions || satisfactionSettings.questions.length === 0) && (
                    <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg">
                      No custom questions added. The default rating question will be used.
                    </p>
                  )}
                </div>

                {/* Thank You Message */}
                <div className="space-y-2">
                  <Label>Thank You Message</Label>
                  <Textarea
                    value={satisfactionSettings.thank_you_message}
                    onChange={(e) => setSatisfactionSettings({
                      ...satisfactionSettings,
                      thank_you_message: e.target.value
                    })}
                    placeholder="Thank you message after survey completion..."
                    rows={2}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500">Sent after the customer completes the survey</p>
                </div>

                {/* Preview */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Preview</h4>
                  <div className="bg-[#393d8a]/5 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 bg-[#393d8a] rounded-full flex items-center justify-center text-white text-xs">
                        PA
                      </div>
                      <div className="flex-1 bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-sm text-gray-700">{satisfactionSettings.survey_message}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
            {[
              { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive notifications via email' },
              { key: 'push_notifications', label: 'Push Notifications', desc: 'Receive browser push notifications' },
              { key: 'sound_alerts', label: 'Sound Alerts', desc: 'Play sound for new messages' }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{item.label}</p>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
                <Switch
                  checked={settings.notifications?.[item.key] ?? true}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, [item.key]: checked }
                  })}
                />
              </div>
            ))}
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h3>
            
            {/* Personal Security - Password Change */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#393d8a]" />
                Change Your Password
              </h4>
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={passwordForm?.current || ''}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                    placeholder="Enter current password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm?.new || ''}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                    placeholder="Enter new password (min 8 characters)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm?.confirm || ''}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
                {passwordError && (
                  <p className="text-sm text-red-500">{passwordError}</p>
                )}
                {passwordSuccess && (
                  <p className="text-sm text-green-500">{passwordSuccess}</p>
                )}
                <Button 
                  onClick={handleChangePassword}
                  disabled={changingPassword || !passwordForm?.current || !passwordForm?.new || !passwordForm?.confirm}
                  className="bg-[#393d8a] hover:bg-[#393d8a]/90"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </div>

            {/* Personal Security - MFA */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#393d8a]" />
                    Two-Factor Authentication (Email OTP)
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    When enabled, you'll receive a verification code via email each time you log in.
                  </p>
                </div>
                <Switch
                  checked={userMfaEnabled}
                  onCheckedChange={handleToggleMfa}
                  disabled={togglingMfa}
                />
              </div>
              {userMfaEnabled && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    ✓ MFA is enabled. You will receive a verification code to your email on each login.
                  </p>
                </div>
              )}
            </div>

            {/* Admin-only: Global Security Settings */}
            {user?.role === 'admin' && (
              <>
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-medium text-gray-900 mb-4">Global Security Settings (Admin)</h4>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Require Email Verification</p>
                    <p className="text-sm text-gray-500">New users must verify their email before logging in</p>
                  </div>
                  <Switch
                    checked={settings.security?.require_email_verification ?? true}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      security: { ...settings.security, require_email_verification: checked }
                    })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Session Timeout (minutes)</Label>
                  <Select
                    value={String(settings.security?.session_timeout || 30)}
                    onValueChange={(value) => setSettings({
                      ...settings,
                      security: { ...settings.security, session_timeout: parseInt(value) }
                    })}
                  >
                    <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
        );

      case 'business-hours':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Hours</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Opening Time</Label>
                <Input
                  type="time"
                  value={settings.business_hours?.start_time || '08:00'}
                  onChange={(e) => setSettings({
                    ...settings,
                    business_hours: { ...settings.business_hours, start_time: e.target.value }
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Closing Time</Label>
                <Input
                  type="time"
                  value={settings.business_hours?.end_time || '18:00'}
                  onChange={(e) => setSettings({
                    ...settings,
                    business_hours: { ...settings.business_hours, end_time: e.target.value }
                  })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Working Days</Label>
              <div className="flex gap-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <button
                    key={day}
                    onClick={() => {
                      const days = settings.business_hours?.work_days || [];
                      const newDays = days.includes(day)
                        ? days.filter(d => d !== day)
                        : [...days, day];
                      setSettings({
                        ...settings,
                        business_hours: { ...settings.business_hours, work_days: newDays }
                      });
                    }}
                    className={cn(
                      "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                      (settings.business_hours?.work_days || []).includes(day)
                        ? "bg-[#393d8a] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Appearance Settings</h3>
            <p className="text-gray-500">Brand colors are set to Post Aruba theme: Blue (#393d8a), Yellow (#eeb224), Green (#30a34b)</p>
            <div className="flex gap-4">
              <div className="w-16 h-16 bg-[#393d8a] rounded-lg" title="#393d8a" />
              <div className="w-16 h-16 bg-[#eeb224] rounded-lg" title="#eeb224" />
              <div className="w-16 h-16 bg-[#30a34b] rounded-lg" title="#30a34b" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#393d8a]" />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50 flex">
      {/* Settings Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Settings</h1>
        </div>
        <nav className="p-2">
          {settingsSections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors mb-1",
                  activeSection === section.id
                    ? "bg-[#393d8a]/10 text-[#393d8a]"
                    : "text-gray-600 hover:bg-gray-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{section.label}</span>
                </div>
                <ChevronRight className={cn(
                  "w-4 h-4 transition-transform",
                  activeSection === section.id && "rotate-90"
                )} />
              </button>
            );
          })}
        </nav>
      </div>

      {/* Settings Content */}
      <div className="flex-1 p-6">
        <div className="max-w-3xl">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            {renderSection()}
          </div>
          {activeSection !== 'channels' && (
            <div className="mt-6 flex justify-end">
              <Button 
                className="gap-2 bg-[#eeb224] hover:bg-[#eeb224]/90"
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Channel Dialogs */}
      <ChannelDialog 
        open={channelDialog.open}
        type={channelDialog.type}
        editing={channelDialog.editing}
        onClose={() => setChannelDialog({ open: false, type: null, editing: null })}
        onSave={handleSaveChannel}
      />
      <RoutingRuleDialog
        open={routingDialog.open}
        editing={routingDialog.editing}
        ruleType={routingDialog.ruleType}
        users={allUsers}
        senders={whatsappChannels}
        onClose={() => setRoutingDialog({ open: false, editing: null })}
        onSave={handleSaveRoutingRule}
      />
      <TemplateDialog
        open={templateDialog.open}
        editing={templateDialog.editing}
        onClose={() => setTemplateDialog({ open: false, editing: null })}
        onSave={handleSaveTemplate}
      />
      <UserSenderDialog
        open={userSenderDialog.open}
        user={userSenderDialog.user}
        senders={whatsappChannels}
        onClose={() => setUserSenderDialog({ open: false, user: null })}
        onSave={handleUpdateUserSenders}
      />
    </div>
  );
};

// Channel Card Component
const ChannelCard = ({ channel, type, icon, onEdit, onDelete, onTest }) => (
  <div className="p-4 border border-gray-200 rounded-lg bg-white" data-testid={`channel-card-${channel.id}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">{channel.name}</p>
            {channel.is_default && (
              <span className="text-xs bg-[#eeb224]/20 text-[#eeb224] px-1.5 py-0.5 rounded font-medium">Default</span>
            )}
            {channel.is_connected ? (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <CheckCircle className="w-3 h-3" /> Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <XCircle className="w-3 h-3" /> Not connected
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">
            {type === 'whatsapp' && channel.phone_number}
            {type === 'facebook' && (channel.page_name || channel.page_id)}
            {type === 'email' && channel.email_address}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onTest} data-testid={`test-channel-${channel.id}`}>
          Test
        </Button>
        <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-channel-${channel.id}`}>
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={onDelete} data-testid={`delete-channel-${channel.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);

// Channel Dialog Component
const ChannelDialog = ({ open, type, editing, onClose, onSave }) => {
  const [data, setData] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setData({
        ...editing,
        // Map whatsapp fields
        whatsapp_number: editing.phone_number || '',
        is_default: editing.is_default || false,
      });
    } else {
      setData({
        name: '',
        whatsapp_number: '',
        is_default: false,
        phone_number: '',
        page_name: '',
        page_id: '',
        page_access_token: '',
        app_secret: '',
        email_address: '',
        provider: 'smtp',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_use_tls: true,
        api_key: ''
      });
    }
    setShowSecrets({});
  }, [editing, open]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(type, data);
    setSaving(false);
  };

  const toggleSecret = (key) => setShowSecrets({ ...showSecrets, [key]: !showSecrets[key] });

  const renderFields = () => {
    switch (type) {
      case 'whatsapp':
        return (
          <>
            <div className="space-y-2">
              <Label>Sender Name</Label>
              <Input value={data.name || ''} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="e.g., Customer Support" data-testid="sender-name-input" />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp Number</Label>
              <Input value={data.whatsapp_number || ''} onChange={(e) => setData({ ...data, whatsapp_number: e.target.value })} placeholder="+297 567 3856" data-testid="sender-number-input" />
              <p className="text-xs text-gray-500">Include country code. Must match a number registered in your Twilio account.</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Switch
                checked={data.is_default || false}
                onCheckedChange={(checked) => setData({ ...data, is_default: checked })}
                data-testid="sender-default-switch"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Default Sender</p>
                <p className="text-xs text-gray-500">Use this number for outgoing messages when no specific sender is set</p>
              </div>
            </div>
          </>
        );

      case 'facebook':
        return (
          <>
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input value={data.name || ''} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="e.g., Post Aruba Support" data-testid="fb-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Facebook Page Name</Label>
              <Input value={data.page_name || ''} onChange={(e) => setData({ ...data, page_name: e.target.value })} placeholder="Post Aruba" data-testid="fb-page-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Page ID</Label>
              <Input value={data.page_id || ''} onChange={(e) => setData({ ...data, page_id: e.target.value })} placeholder="Enter Facebook Page ID" data-testid="fb-page-id-input" />
              <p className="text-xs text-gray-500">The Page ID from your Facebook Page settings. This must match the page connected in your Twilio Messenger channel.</p>
            </div>
            <div className="space-y-2">
              <Label>Page Access Token <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input 
                type="password"
                value={data.page_access_token || ''} 
                onChange={(e) => setData({ ...data, page_access_token: e.target.value })} 
                placeholder="Enter Page Access Token for profile name lookup" 
                data-testid="fb-access-token-input" 
              />
              <p className="text-xs text-gray-500">Required to fetch customer names from Facebook profiles. Get it from Facebook Page Settings → Messenger → Access Tokens.</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Switch
                checked={data.is_default || false}
                onCheckedChange={(checked) => setData({ ...data, is_default: checked })}
                data-testid="fb-default-switch"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Default Page</p>
                <p className="text-xs text-gray-500">Use this page for outgoing Messenger messages when no specific page is set</p>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Setup:</strong> Connect this Facebook Page to your Twilio Messaging Service in the Twilio Console under Channels &gt; Facebook Messenger.
              </p>
            </div>
          </>
        );

      case 'email':
        return (
          <>
            <div className="space-y-2">
              <Label>Channel Name</Label>
              <Input value={data.name || ''} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="e.g., Support Email" />
            </div>
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input type="email" value={data.email_address || ''} onChange={(e) => setData({ ...data, email_address: e.target.value })} placeholder="support@postaruba.com" />
            </div>
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select value={data.provider || 'smtp'} onValueChange={(value) => setData({ ...data, provider: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="smtp">SMTP</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="mailgun">Mailgun</SelectItem>
                  <SelectItem value="aws_ses">AWS SES</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {data.provider === 'smtp' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input value={data.smtp_host || ''} onChange={(e) => setData({ ...data, smtp_host: e.target.value })} placeholder="smtp.gmail.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input type="number" value={data.smtp_port || 587} onChange={(e) => setData({ ...data, smtp_port: parseInt(e.target.value) })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>SMTP Username</Label>
                  <Input value={data.smtp_username || ''} onChange={(e) => setData({ ...data, smtp_username: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Password</Label>
                  <div className="relative">
                    <Input 
                      type={showSecrets.smtp_password ? 'text' : 'password'} 
                      value={data.smtp_password || ''} 
                      onChange={(e) => setData({ ...data, smtp_password: e.target.value })} 
                      className="pr-10"
                    />
                    <button type="button" onClick={() => toggleSecret('smtp_password')} className="absolute right-3 top-1/2 -translate-y-1/2">
                      {showSecrets.smtp_password ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="relative">
                  <Input 
                    type={showSecrets.api_key ? 'text' : 'password'} 
                    value={data.api_key || ''} 
                    onChange={(e) => setData({ ...data, api_key: e.target.value })} 
                    className="pr-10"
                  />
                  <button type="button" onClick={() => toggleSecret('api_key')} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showSecrets.api_key ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    const action = editing ? 'Edit' : 'Add';
    switch (type) {
      case 'whatsapp': return `${action} WhatsApp Sender`;
      case 'facebook': return `${action} Facebook Page`;
      case 'email': return `${action} Email Channel`;
      default: return 'Channel';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            Configure your {type} channel settings
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {renderFields()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#393d8a] hover:bg-[#393d8a]/90">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {editing ? 'Update' : 'Create'} Channel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Routing Rule Dialog Component
const RoutingRuleDialog = ({ open, editing, ruleType, users, senders, onClose, onSave }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setData({ ...editing });
    } else {
      setData({
        rule_type: ruleType || 'platform',
        platform: ruleType === 'whatsapp_sender' ? 'whatsapp' : '',
        whatsapp_sender_id: '',
        primary_agent_id: '',
        secondary_agent_id: '',
        active: true,
      });
    }
  }, [editing, open, ruleType]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(data);
    setSaving(false);
  };

  const agentOptions = users.filter(u => u.is_active !== false);
  const isPlatform = (data.rule_type || ruleType) === 'platform';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit' : 'Add'} {isPlatform ? 'Platform' : 'WhatsApp Sender'} Routing Rule</DialogTitle>
          <DialogDescription>
            {isPlatform
              ? 'Route all incoming conversations from a platform to a specific agent.'
              : 'Route conversations from a specific WhatsApp sender number to an agent.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isPlatform ? (
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={data.platform || ''} onValueChange={(v) => setData({ ...data, platform: v })} disabled={!!editing}>
                <SelectTrigger data-testid="routing-platform-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>WhatsApp Sender</Label>
              <Select value={data.whatsapp_sender_id || ''} onValueChange={(v) => setData({ ...data, whatsapp_sender_id: v, platform: 'whatsapp' })} disabled={!!editing}>
                <SelectTrigger data-testid="routing-sender-select"><SelectValue placeholder="Select sender" /></SelectTrigger>
                <SelectContent>
                  {senders.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.phone_number})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Primary Agent</Label>
            <Select value={data.primary_agent_id || ''} onValueChange={(v) => setData({ ...data, primary_agent_id: v })}>
              <SelectTrigger data-testid="routing-primary-agent"><SelectValue placeholder="Select primary agent" /></SelectTrigger>
              <SelectContent>
                {agentOptions.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">This agent will receive conversations first.</p>
          </div>

          <div className="space-y-2">
            <Label>Secondary Agent (Fallback)</Label>
            <Select value={data.secondary_agent_id || 'none'} onValueChange={(v) => setData({ ...data, secondary_agent_id: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="routing-secondary-agent"><SelectValue placeholder="Select secondary agent (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {agentOptions.filter(u => u.id !== data.primary_agent_id).map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Receives conversations when the primary agent is offline.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !data.primary_agent_id || (isPlatform ? !data.platform : !data.whatsapp_sender_id)}
            className="bg-[#393d8a] hover:bg-[#393d8a]/90"
            data-testid="save-routing-rule-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {editing ? 'Update' : 'Create'} Rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Template Dialog Component
const TemplateDialog = ({ open, editing, onClose, onSave }) => {
  const [data, setData] = useState({ name: '', category: 'general', content: '', channels: ['all'] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setData({
        name: editing.name || '',
        category: editing.category || 'general',
        content: editing.content || '',
        channels: editing.channels || ['all'],
      });
    } else {
      setData({ name: '', category: 'general', content: '', channels: ['all'] });
    }
  }, [editing, open]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(data);
    setSaving(false);
  };

  const toggleChannel = (ch) => {
    if (ch === 'all') {
      setData({ ...data, channels: ['all'] });
      return;
    }
    let current = data.channels.filter(c => c !== 'all');
    if (current.includes(ch)) {
      current = current.filter(c => c !== ch);
    } else {
      current.push(ch);
    }
    setData({ ...data, channels: current.length === 0 ? ['all'] : current });
  };

  const channelOptions = [
    { id: 'all', label: 'All Channels' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'email', label: 'Email' },
    { id: 'chat', label: 'Live Chat' },
  ];

  const categoryOptions = ['general', 'greeting', 'faq', 'escalation', 'closing', 'tracking', 'billing'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit' : 'Create'} Message Template</DialogTitle>
          <DialogDescription>Templates let agents reply faster. They can edit the text before sending.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Template Name</Label>
            <Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="e.g., Welcome Greeting" data-testid="template-name-input" />
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={data.category} onValueChange={(v) => setData({ ...data, category: v })}>
              <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categoryOptions.map(c => (
                  <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Message Content</Label>
            <Textarea
              value={data.content}
              onChange={(e) => setData({ ...data, content: e.target.value })}
              placeholder="Hello! Thank you for contacting Post Aruba. How can I help you today?"
              rows={4}
              className="resize-none"
              data-testid="template-content-input"
            />
            <p className="text-xs text-gray-500">Agents can edit this text before sending it in a conversation.</p>
          </div>

          <div className="space-y-2">
            <Label>Available on Channels</Label>
            <div className="flex flex-wrap gap-2">
              {channelOptions.map(ch => (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => toggleChannel(ch.id)}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-full border transition-colors",
                    data.channels.includes(ch.id)
                      ? "bg-[#393d8a] text-white border-[#393d8a]"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  )}
                  data-testid={`template-channel-${ch.id}`}
                >
                  {ch.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !data.name.trim() || !data.content.trim()}
            className="bg-[#393d8a] hover:bg-[#393d8a]/90"
            data-testid="save-template-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {editing ? 'Update' : 'Create'} Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User WhatsApp Sender Permissions Dialog
const UserSenderDialog = ({ open, user, senders, onClose, onSave }) => {
  const [selectedSenders, setSelectedSenders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [allowAll, setAllowAll] = useState(true);

  useEffect(() => {
    if (user) {
      const userSenders = user.allowed_whatsapp_senders || [];
      setSelectedSenders(userSenders);
      setAllowAll(userSenders.length === 0);
    }
  }, [user, open]);

  const handleToggleSender = (phoneNumber) => {
    setSelectedSenders(prev => 
      prev.includes(phoneNumber)
        ? prev.filter(p => p !== phoneNumber)
        : [...prev, phoneNumber]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    // If "Allow All" is checked, save empty array (no restrictions)
    await onSave(user.id, allowAll ? [] : selectedSenders);
    setSaving(false);
  };

  const handleAllowAllChange = (checked) => {
    setAllowAll(checked);
    if (checked) {
      setSelectedSenders([]);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Configure WhatsApp Sender Access</DialogTitle>
          <DialogDescription>
            Choose which WhatsApp numbers <strong>{user.name}</strong> can use when replying to conversations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Allow All Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900 text-sm">Allow All Senders</p>
              <p className="text-xs text-gray-500">Agent can reply from any WhatsApp number</p>
            </div>
            <Switch
              checked={allowAll}
              onCheckedChange={handleAllowAllChange}
              data-testid="allow-all-senders-switch"
            />
          </div>

          {/* Sender Selection (only shown when restricted) */}
          {!allowAll && (
            <div className="space-y-2">
              <Label className="text-sm">Allowed Senders</Label>
              <p className="text-xs text-gray-500 mb-2">Select which WhatsApp numbers this agent can use:</p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {senders.length === 0 ? (
                  <p className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded">
                    No WhatsApp senders configured. Add senders in Channels first.
                  </p>
                ) : (
                  senders.map(sender => (
                    <label
                      key={sender.id}
                      className={cn(
                        "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors",
                        selectedSenders.includes(sender.phone_number)
                          ? "border-green-500 bg-green-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                      data-testid={`sender-checkbox-${sender.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSenders.includes(sender.phone_number)}
                        onChange={() => handleToggleSender(sender.phone_number)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{sender.name}</p>
                        <p className="text-xs text-gray-500">{sender.phone_number}</p>
                      </div>
                      {sender.is_default && (
                        <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Default</span>
                      )}
                    </label>
                  ))
                )}
              </div>
              {!allowAll && selectedSenders.length === 0 && senders.length > 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Warning: No senders selected. Agent won't be able to reply to WhatsApp conversations.
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || (!allowAll && selectedSenders.length === 0 && senders.length > 0)}
            className="bg-[#30a34b] hover:bg-[#30a34b]/90"
            data-testid="save-user-senders-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsView;
